import React, { useEffect, useState, useRef } from "react";
import { useVipStore } from "../context/VipContext";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";

const BASE_URL = process.env.REACT_APP_BASE_URL;

export default function VipProfile() {
  const navigate = useNavigate();
  const { handleEdit, setSelectedVip, selectedVip } = useVipStore();

  const [userDetails, setUserDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileImage, setProfileImage] = useState(null);

  const fileRef = useRef(null);

  useEffect(() => {
    fetchProfile();
  }, []);

  /* ---------------- FETCH PROFILE ---------------- */
  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem("vipToken");
      if (!token) return;

      const decoded = jwtDecode(token);
      const username = decoded.username || decoded.sub || decoded.email;

      const res = await axios.get(`${BASE_URL}/api/categories/profile`, {
        params: { username },
        headers: { Authorization: `Bearer ${token}` },
      });

      const profile = Array.isArray(res.data) ? res.data[0] : res.data;

      setUserDetails(profile);
      setSelectedVip(profile);
      localStorage.setItem("selectedVip", JSON.stringify(profile));

      if (profile?.url) {
        setProfileImage(profile.url);
      }
    } catch (err) {
      console.log("Profile Fetch Error:", err);
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- IMAGE UPLOAD ---------------- */
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedVip?.id) return;

    const token = localStorage.getItem("vipToken");
    const formData = new FormData();
    formData.append("imaged", file);

    try {
      const res = await axios.post(
        `${BASE_URL}/api/profile/upload/${selectedVip.id}/vip`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      const imageUrl = res.data;

      if (imageUrl) {
        setProfileImage(imageUrl);

        const updatedVip = {
          ...selectedVip,
          url: imageUrl,
        };

        setSelectedVip(updatedVip);
        localStorage.setItem("selectedVip", JSON.stringify(updatedVip));
      }
    } catch (err) {
      console.log("Image Upload Error:", err.response?.data || err.message);
    }
  };

  /* ---------------- IMAGE PREVIEW ---------------- */
  const openImage = () => {
    if (profileImage) {
      window.open(profileImage, "_blank");
    }
  };

  /* ---------------- LOADING ---------------- */
  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading your profile...</p>
      </div>
    );
  }

  /* ---------------- ERROR ---------------- */
  if (!userDetails) {
    return (
      <div className="error-container">
        <h4>Unable to load profile.</h4>
        <p>Please try logging in again.</p>
      </div>
    );
  }

  /* ---------------- UI ---------------- */
  return (
    <>
      <div className="profile-wrapper">
        {/* Header */}
        <header className="profile-header">
          <div className="header-content">
            <h1>
              <i className="fas fa-user-circle"></i> VIP Profile
            </h1>
            <p>Manage and view your account details</p>
          </div>
        </header>

        {/* Profile Card */}
        <div className="profile-card">
          <div className="profile-grid">
            {/* Left: Image Section */}
            <div className="image-section">
              <div className="image-container">
                <img
                  src={profileImage || "https://via.placeholder.com/180?text=VIP"}
                  alt="VIP Profile"
                  className="profile-img"
                  onClick={openImage}
                />
                <div
                  className="camera-icon"
                  onClick={() => fileRef.current.click()}
                >
                  <i className="fas fa-camera"></i>
                </div>
              </div>
              <input
                type="file"
                accept="image/*"
                ref={fileRef}
                onChange={handleImageUpload}
                style={{ display: "none" }}
              />
              <h2 className="vip-name">{userDetails.name}</h2>
              <p className="vip-role">VIP Account</p>
            </div>

            {/* Right: Details Section */}
            <div className="details-section">
              <div className="details-grid">
                {[
                  ["VIP ID", userDetails.id],
                  ["Username", userDetails.username],
                  ["Email", userDetails.email],
                  ["Contact No", userDetails.contactno || "—"],
                  ["Gender", userDetails.gender || "—"],
                  ["Aadhaar Number", userDetails.adharNo || "—"],
                  [
                    "Status",
                    <span
                      className="status-badge"
                      style={{
                        backgroundColor:
                          userDetails.status === "Active"
                            ? "#10b981"
                            : "#ef4444",
                      }}
                    >
                      {userDetails.status || "Inactive"}
                    </span>,
                  ],
                ].map(([label, value], i) => (
                  <div className="detail-item" key={i}>
                    <span className="label">{label}:</span>
                    <span className="value">{value}</span>
                  </div>
                ))}
              </div>

              <button
                className="edit-button"
                onClick={() => {
                  handleEdit(userDetails);
                  navigate("/vipedit");
                }}
              >
                <i className="fas fa-edit"></i>
                Edit Profile
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modern Light Purple Responsive Styling */}
      <style jsx>{`
        .profile-wrapper {
          min-height: 100vh;
          background: linear-gradient(135deg, #e0d4ff 0%, #f0e6ff 100%);
          padding: 2rem;
          color: #4c1d95;
        }

        .profile-header {
          text-align: center;
          margin-bottom: 2.5rem;
        }

        .header-content h1 {
          font-size: 2.5rem;
          font-weight: 700;
          color: #9333ea;
          margin: 0;
        }

        .header-content p {
          font-size: 1.1rem;
          opacity: 0.9;
          margin-top: 0.5rem;
          color: #7c3aed;
        }

        .profile-card {
          max-width: 1000px;
          margin: 0 auto;
          background: rgba(255, 255, 255, 0.85);
          backdrop-filter: blur(12px);
          border-radius: 20px;
          padding: 2.5rem;
          box-shadow: 0 15px 40px rgba(168, 85, 247, 0.25);
          border: 1px solid rgba(168, 85, 247, 0.4);
        }

        .profile-grid {
          display: grid;
          grid-template-columns: 1fr 2fr;
          gap: 3rem;
          align-items: start;
        }

        .image-section {
          text-align: center;
        }

        .image-container {
          position: relative;
          display: inline-block;
          margin-bottom: 1.5rem;
        }

        .profile-img {
          width: 180px;
          height: 180px;
          border-radius: 50%;
          object-fit: cover;
          border: 5px solid #a855f7;
          box-shadow: 0 10px 25px rgba(168, 85, 247, 0.3);
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .profile-img:hover {
          transform: scale(1.05);
          box-shadow: 0 15px 35px rgba(168, 85, 247, 0.4);
        }

        .camera-icon {
          position: absolute;
          bottom: 10px;
          right: 10px;
          background: #a855f7;
          color: white;
          width: 42px;
          height: 42px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.2rem;
          cursor: pointer;
          box-shadow: 0 4px 15px rgba(168, 85, 247, 0.4);
          transition: all 0.3s ease;
        }

        .camera-icon:hover {
          background: #9333ea;
          transform: scale(1.1);
        }

        .vip-name {
          font-size: 1.8rem;
          font-weight: 700;
          color: #581c87;
          margin: 0.5rem 0;
        }

        .vip-role {
          font-size: 1.1rem;
          color: #7c3aed;
          opacity: 0.9;
        }

        .details-section {
          display: flex;
          flex-direction: column;
          justify-content: space-between;
        }

        .details-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 1rem;
          margin-bottom: 2rem;
        }

        .detail-item {
          display: flex;
          justify-content: space-between;
          padding: 0.8rem 0;
          border-bottom: 1px solid rgba(168, 85, 247, 0.2);
        }

        .detail-item:last-child {
          border-bottom: none;
        }

        .label {
          font-weight: 600;
          color: #6b21a8;
          min-width: 140px;
        }

        .value {
          font-weight: 500;
          color: #581c87;
          text-align: right;
        }

        .status-badge {
          padding: 0.5rem 1rem;
          border-radius: 50px;
          color: white;
          font-weight: 600;
          font-size: 0.9rem;
        }

        .edit-button {
          align-self: flex-start;
          background: #a855f7;
          color: white;
          border: none;
          padding: 0.9rem 1.8rem;
          border-radius: 12px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 0.6rem;
          transition: all 0.3s ease;
          box-shadow: 0 6px 20px rgba(168, 85, 247, 0.3);
        }

        .edit-button:hover {
          background: #9333ea;
          transform: translateY(-3px);
          box-shadow: 0 10px 25px rgba(168, 85, 247, 0.4);
        }

        .loading-container,
        .error-container {
          text-align: center;
          padding: 4rem;
          color: #9333ea;
        }

        .spinner {
          width: 60px;
          height: 60px;
          border: 5px solid #e9d5ff;
          border-top: 5px solid #a855f7;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 1.5rem;
        }

        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }

        /* Responsive Design */
        @media (max-width: 992px) {
          .profile-grid {
            grid-template-columns: 1fr;
            text-align: center;
          }

          .details-section {
            align-items: center;
          }

          .edit-button {
            align-self: center;
          }
        }

        @media (max-width: 576px) {
          .profile-wrapper {
            padding: 1rem;
          }

          .profile-card {
            padding: 1.5rem;
          }

          .profile-img {
            width: 150px;
            height: 150px;
          }

          .header-content h1 {
            font-size: 2rem;
          }

          .details-grid {
            grid-template-columns: 1fr;
          }

          .detail-item {
            justify-content: flex-start;
            gap: 1rem;
          }

          .value {
            text-align: left;
          }
        }
      `}</style>
    </>
  );
}

// import React, { useEffect, useState, useRef } from "react";
// import { useVipStore } from "../context/VipContext";
// import axios from "axios";
// import { useNavigate } from "react-router-dom";
// import { jwtDecode } from "jwt-decode";

// const BASE_URL = process.env.REACT_APP_BASE_URL;

// export default function VipProfile() {
//   const navigate = useNavigate();
//   const { handleEdit, setSelectedVip, selectedVip } = useVipStore();

//   const [userDetails, setUserDetails] = useState(null);
//   const [loading, setLoading] = useState(true);
//   const [profileImage, setProfileImage] = useState(null);

//   const fileRef = useRef(null);

//   useEffect(() => {
//     fetchProfile();
//   }, []);

//   /* ---------------- FETCH PROFILE ---------------- */
//   const fetchProfile = async () => {
//     try {
//       const token = localStorage.getItem("vipToken");
//       if (!token) return;

//       const decoded = jwtDecode(token);
//       const username = decoded.username || decoded.sub || decoded.email;

//       const res = await axios.get(`${BASE_URL}/api/categories/profile`, {
//         params: { username },
//         headers: { Authorization: `Bearer ${token}` },
//       });

//       const profile = Array.isArray(res.data) ? res.data[0] : res.data;

//       setUserDetails(profile);
//       setSelectedVip(profile);
//       localStorage.setItem("selectedVip", JSON.stringify(profile));

//       //  SET PROFILE IMAGE FROM URL
//       if (profile?.url) {
//         setProfileImage(profile.url);
//       }
//     } catch (err) {
//       console.log("Profile Fetch Error:", err);
//     } finally {
//       setLoading(false);
//     }
//   };

//   /* ---------------- IMAGE UPLOAD ---------------- */
//   const handleImageUpload = async (e) => {
//     const file = e.target.files[0];
//     if (!file || !selectedVip?.id) return;

//     const token = localStorage.getItem("vipToken");
//     const formData = new FormData();
//     formData.append("imaged", file);

//     try {
//       const res = await axios.post(
//         `${BASE_URL}/api/profile/upload/${selectedVip.id}/vip`,
//         formData,
//         {
//           headers: {
//             Authorization: `Bearer ${token}`,
//             "Content-Type": "multipart/form-data",
//           },
//         }
//       );

//       // console.log("UPLOAD RESPONSE:",await res.data);

//       //  YOUR API RETURNS res.data.url
//       const imageUrl = res.data;

//       if (imageUrl) {
//         //  UPDATE UI INSTANTLY
//         setProfileImage(imageUrl);

//         //  UPDATE CONTEXT + LOCALSTORAGE
//         const updatedVip = {
//           ...selectedVip,
//           url: imageUrl,
//         };

//         setSelectedVip(updatedVip);
//         localStorage.setItem(
//           "selectedVip",
//           JSON.stringify(updatedVip)
//         );
//       }
//     } catch (err) {
//       console.log(
//         "Image Upload Error:",
//         err.response?.data || err.message
//       );
//     }
//   };

//   /* ---------------- IMAGE PREVIEW ---------------- */
//   const openImage = () => {
//     if (profileImage) {
//       window.open(profileImage, "_blank");
//     }
//   };

//   /* ---------------- LOADING ---------------- */
//   if (loading) {
//     return (
//       <div className="text-center py-5">
//         <i className="fa fa-spinner fa-spin fa-2x text-primary mb-3"></i>
//         <h5>Loading profile...</h5>
//       </div>
//     );
//   }

//   /* ---------------- ERROR ---------------- */
//   if (!userDetails) {
//     return (
//       <div className="text-center py-5 text-danger">
//         <h4>Unable to load profile.</h4>
//       </div>
//     );
//   }

//   /* ---------------- UI ---------------- */
//   return (
//     <div style={styles.pageContainer}>
//       <div style={styles.header}>
//         <h2 style={styles.headerTitle}>
//           <i className="fa fa-user-circle me-2"></i> Vip Profile
//         </h2>
//         <p style={styles.headerSubtitle}>
//           Overview of Vip account details
//         </p>
//       </div>

//       <div style={styles.card}>
//         <div style={styles.cardBody}>
//           <div style={styles.profileRow}>
//             {/* -------- IMAGE -------- */}
//             <div style={styles.leftBox}>
//               <div style={styles.imageWrapper}>
//                 {/*  CLICK IMAGE TO OPEN */}
//                 <img
//                   src={
//                     profileImage 
//                   }
//                   alt="Profile"
//                   style={styles.profileImage}
//                   onClick={openImage}
//                 />

//                 {/*  CAMERA BUTTON FOR UPLOAD */}
//                 <div
//                   style={styles.cameraOverlay}
//                   onClick={() => fileRef.current.click()}
//                 >
//                   <i className="fa fa-camera"></i>
//                 </div>
//               </div>

//               {/*  HIDDEN INPUT */}
//               <input
//                 type="file"
//                 accept="image/*"
//                 ref={fileRef}
//                 onChange={handleImageUpload}
//                 style={{ display: "none" }}
//               />

//               <h4 style={styles.name}>{userDetails.name}</h4>
//               <p style={styles.role}>Vip Detail</p>
//             </div>

//             {/* -------- DETAILS -------- */}
//             <div style={styles.rightBox}>
//               {[
//                 ["Vip ID", userDetails.id],
//                 ["Username", userDetails.username],
//                 ["Email", userDetails.email],
//                 ["Contact No", userDetails.contactno],
//                 ["Gender", userDetails.gender],
//                 ["Aadhaar Number", userDetails.adharNo],
//                 [
//                   "Status",
//                   <span
//                     style={{
//                       ...styles.statusBadge,
//                       background:
//                         userDetails.status === "Active"
//                           ? "#28a745"
//                           : "#dc3545",
//                     }}
//                   >
//                     {userDetails.status}
//                   </span>,
//                 ],
//               ].map(([label, value], i) => (
//                 <div style={styles.detailRow} key={i}>
//                   <div style={styles.detailLabel}>{label}:</div>
//                   <div style={styles.detailValue}>{value}</div>
//                 </div>
//               ))}

//               <div style={{ marginTop: "20px" }}>
//                 <button
//                   style={styles.editBtn}
//                   onClick={() => {
//                     handleEdit(userDetails);
//                     navigate("/vipedit");
//                   }}
//                 >
//                   <i className="fa fa-edit me-1"></i> Edit Profile
//                 </button>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// }

// /* ---------------- STYLES ---------------- */
// const styles = {
//   pageContainer: {
//     padding: "30px",
//     minHeight: "100vh",
//     background: "rgba(255,255,255,0.15)",
//     backdropFilter: "blur(6px)",
//   },
//   header: {
//     marginBottom: "25px",
//     padding: "20px",
//     background: "linear-gradient(135deg, #4e54c8, #8f94fb)",
//     borderRadius: "14px",
//     color: "white",
//     boxShadow: "0 6px 18px rgba(0,0,0,0.15)",
//   },
//   headerTitle: { margin: 0, fontSize: "26px", fontWeight: "700" },
//   headerSubtitle: { marginTop: "5px", opacity: 0.9 },

//   card: {
//     background: "rgba(255,255,255,0.55)",
//     borderRadius: "18px",
//     padding: "25px",
//     backdropFilter: "blur(8px)",
//     boxShadow: "0 8px 20px rgba(0,0,0,0.15)",
//   },

//   profileRow: {
//     display: "flex",
//     gap: "40px",
//     flexWrap: "wrap",
//     alignItems: "center",
//   },

//   leftBox: { flex: "1", textAlign: "center" },

//   imageWrapper: {
//     position: "relative",
//     width: "140px",
//     margin: "auto",
//   },

//   profileImage: {
//     width: "140px",
//     height: "140px",
//     borderRadius: "50%",
//     objectFit: "cover",
//     border: "3px solid #4e54c8",
//     cursor: "pointer",
//   },

//   cameraOverlay: {
//     position: "absolute",
//     bottom: "5px",
//     right: "5px",
//     background: "#1e73be",
//     color: "white",
//     borderRadius: "50%",
//     width: "32px",
//     height: "32px",
//     display: "flex",
//     alignItems: "center",
//     justifyContent: "center",
//     fontSize: "14px",
//     cursor: "pointer",
//   },

//   name: { fontSize: "22px", fontWeight: "700", marginTop: "10px" },
//   role: { fontSize: "15px", color: "#777" },

//   rightBox: { flex: "2" },

//   detailRow: { display: "flex", marginBottom: "12px" },
//   detailLabel: { width: "150px", fontWeight: "600", color: "#333" },
//   detailValue: { flex: 1, fontWeight: "500", color: "#444" },

//   statusBadge: {
//     padding: "6px 12px",
//     borderRadius: "20px",
//     color: "white",
//     fontWeight: "600",
//   },

//   editBtn: {
//     background: "#1e73be",
//     color: "white",
//     padding: "10px 18px",
//     borderRadius: "10px",
//     border: "none",
//     cursor: "pointer",
//     fontWeight: "600",
//   },
// };
